import React, { createContext, useContext, useEffect, useState } from 'react';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useApiToken } from '../lib/unified-auth';
import type { MultiTenantUser } from '@xobcl/types/multi-tenant-roles';
import { setOrderAuthHeaderProvider } from '../lib/order-api';
import { isAccessGovernanceMode, isTenantAdminSource, resolveServerTenantRole } from '../auth/tenantAccess';
import { hasPermission, canEditContact } from '../../../types/src/multi-tenant-roles';
import { logger } from '../lib/logger';
import { setAuthHeaderProvider } from '../lib/api';
import { config } from '../lib/config';
import { API_LOGIN_REQUEST, getMsalPopupRedirectUri } from '../auth/msal';
import { teamsAuth } from '../lib/teams-auth';
import { setGraphTokenProvider, setTeamsPresenceTokenProvider, setGraphAuthHeaderProvider, setTeamsContext } from '../lib/graph';

interface AuthContextType {
  // Authentication state
  isAuthenticated: boolean;
  isLoading: boolean;
  isInTeams?: boolean; // For Teams context compatibility
  /**
   * True when the SPA is rendered inside a cross-origin frame, i.e. an M365 host
   * (Teams / Outlook / Microsoft 365) on any platform. Unlike `isInTeams` this does
   * not depend on the TeamsJS handshake succeeding, so it stays correct on mobile
   * webviews where the handshake can time out. Browser-only UI and flows must be
   * suppressed whenever this is true.
   */
  isEmbedded: boolean;
  // True once the async Teams-context detection on mount has settled. Consumers
  // should wait for this before rendering environment-specific unauthenticated
  // UI (e.g. the web LandingPage), to avoid a flash inside Teams.
  teamsDetected: boolean;
  /** True while an MSAL interactive operation (popup/redirect) is in flight. */
  isMsalInteracting: boolean;
  
  // User information
  user: MultiTenantUser | null;
  azureAccount: any | null;
  
  // Role and permission checking
  hasPermission: (permission: string, resourceType?: 'site' | 'list', resourceId?: string) => boolean;
  canEditContact: () => boolean;
  canManageList: (listId: string) => boolean;
  canAccessSites: () => boolean;
  canAccessAdmin: () => boolean;
  
  // Legacy compatibility for existing code
  roles: string[];
  
  // Actions
  login: () => Promise<void>;   // Alias for signIn
  logout: () => Promise<void>;  // Alias for signOut
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  getToken: () => Promise<string>; // Expose token getter for API calls
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { instance, accounts, inProgress } = useMsal();
  const { account, getToken } = useApiToken();
  
  const [user, setUser] = useState<MultiTenantUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [roles, setRoles] = useState<string[]>([]);
  const [isInTeams, setIsInTeams] = useState(false);
  // Seeded synchronously: being framed is knowable on the first render, before any
  // async detection, so the app never briefly commits to browser-only UI inside a host.
  const [isEmbedded, setIsEmbedded] = useState(() => {
    try {
      return typeof window !== 'undefined' && window.self !== window.top;
    } catch {
      return true;
    }
  });
  const [teamsAuthenticated, setTeamsAuthenticated] = useState(false);
  // Whether the async Teams-context detection on mount has settled. Until it has,
  // `isInTeams` is still its default `false`, so the app must not commit to the
  // web (non-Teams) LandingPage yet — doing so flashes the landing page inside
  // Teams before detection flips `isInTeams` true.
  const [teamsDetected, setTeamsDetected] = useState(false);

  // Authentication status: MSAL accounts OR host (Teams/Outlook/M365) SSO
  const isAuthenticated = accounts.length > 0 || ((isInTeams || isEmbedded) && teamsAuthenticated);
  const azureAccount = accounts[0] || null;

  // Fetch user data from /api/me endpoint
  const fetchUserData = async (): Promise<void> => {
    if (!isAuthenticated) {
      setUser(null);
      setRoles([]);
      return;
    }

    try {
      // Fetch user data from API
      let token;
      try {
        token = await getToken?.();
        logger.debug('Token acquired', { hasToken: !!token }, 'AUTH');
      } catch (tokenError) {
        logger.error('Failed to get token', tokenError, 'AUTH');
        throw new Error(`Failed to acquire token: ${tokenError}`);
      }
      
      const response = await fetch(`${config.api.baseUrl}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      logger.debug('/api/me response', { status: response.status }, 'AUTH');
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('/api/me error response', { status: response.status, errorText }, 'AUTH');
        throw new Error(`Failed to fetch user data: ${response.status} ${response.statusText}`);
      }

      const userData = await response.json();
      
      // Raw Entra roles (server-validated token roles echoed by /api/me, else the MSAL id-token claim).
      // Only the independent HostingPortal guard reads these. userData.roles holds the *stored* tenant
      // roles and must never be mistaken for Entra roles.
      const azureRoles: string[] = Array.isArray(userData.entraRoles)
        ? userData.entraRoles
        : azureAccount?.idTokenClaims?.roles || [];
      setRoles(azureRoles);

      // The customer-tenant role comes only from the API, which resolves it under the tenant's
      // access-governance mode. There is deliberately no client-side fallback from raw Entra roles:
      // in appManaged mode QuickDial.Admin does not grant tenant-admin, so guessing would drift.
      const tenantRole = resolveServerTenantRole(userData.tenantRole);

      const multiTenantUser: MultiTenantUser = {
        id: userData.userId,
        tenantId: userData.tenantId,
        userId: userData.userId, // Azure AD Object ID
        upn: userData.upn,
        name: userData.name,
        tenantRole,
        effectiveAdminSource: isTenantAdminSource(userData.effectiveAdminSource) ? userData.effectiveAdminSource : 'none',
        accessGovernanceMode: isAccessGovernanceMode(userData.accessGovernanceMode) ? userData.accessGovernanceMode : 'entraOnly',
        // List-level manager flag from API (for granular permissions)
        isListManager: userData.isListManager || false,
        // Initialize empty resource roles (would be populated from backend)
        resourceRoles: {
          sites: {},
          lists: {}
        },
        // Set permissions based on API response or calculate from tenant role
        permissions: userData.permissions || {
          canManageUsers: tenantRole === 'TenantAdmin',
          canCreateSites: tenantRole === 'TenantAdmin' || tenantRole === 'Manager',
          canCreateLists: tenantRole !== 'Guest',
          canViewAllContacts: tenantRole === 'TenantAdmin' || tenantRole === 'Manager'
        }
      };

      setUser(multiTenantUser);
      logger.info('User data loaded successfully', { 
        userId: multiTenantUser.id, 
        tenantRole: multiTenantUser.tenantRole,
        azureRoles,
        userData
      }, 'AUTH');
      
      logger.debug('User loaded', {
        tenantRole: multiTenantUser.tenantRole,
        azureRoles,
        canAccessSites: multiTenantUser.tenantRole === 'TenantAdmin' || multiTenantUser.tenantRole === 'Manager',
        canAccessAdmin: multiTenantUser.tenantRole === 'TenantAdmin'
      }, 'AUTH');

    } catch (error) {
      logger.error('Failed to fetch user data', error, 'AUTH');
      setUser(null);
      setRoles([]);
    }
  };

  // Permission checking functions
  const checkPermission = (permission: string, resourceType?: 'site' | 'list', resourceId?: string): boolean => {
    if (!user) return false;
    return hasPermission(user, permission, resourceType, resourceId);
  };

  const checkCanEditContact = (): boolean => {
    if (!user) return false;
    return canEditContact(user);
  };

  const checkCanManageList = (listId: string): boolean => {
    if (!user) return false;
    // Import the function from multi-tenant-roles when needed
    return user.tenantRole === 'TenantAdmin' || user.resourceRoles.lists[listId] === 'Owner';
  };

  const checkCanAccessSites = (): boolean => {
    if (!user) return false;
    // Users can access Sites/Manage page if:
    // 1. They are a TenantAdmin (full access)
    // 2. They have the Manager tenant role (full access)
    // 3. They are a manager of at least one list (granular access)
    return user.tenantRole === 'TenantAdmin' || user.tenantRole === 'Manager' || user.isListManager === true;
  };

  const checkCanAccessAdmin = (): boolean => {
    if (!user) return false;
    return user.tenantRole === 'TenantAdmin';
  };

  // Authentication actions
  const signIn = async (): Promise<void> => {
    try {
      // Route on `isEmbedded` too: inside an M365 host webview an MSAL popup is
      // blocked outright, so host SSO is the only flow that can succeed even when
      // detection could not confirm which host we are in.
      if (isInTeams || isEmbedded) {
        // Use host SSO authentication (Teams / Outlook / Microsoft 365)
        logger.debug('Attempting host SSO login', { isInTeams, isEmbedded }, 'AUTH');
        const token = await teamsAuth.getAuthToken();
        logger.debug('Host SSO token acquired', { hasToken: !!token }, 'AUTH');

        // Mark as authenticated for the host session
        setTeamsAuthenticated(true);
        
        // Token acquired, now fetch user data
        await fetchUserData();
      } else {
        // Guard against overlapping MSAL interactions (e.g. double-click on Sign In).
        if (inProgress !== InteractionStatus.None) {
          logger.warn(
            'Ignoring sign-in request: an MSAL interaction is already in progress',
            { inProgress },
            'AUTH',
          );
          return;
        }
        // Use MSAL popup for web; redirect the popup to the dedicated lightweight
        // callback route so the full SPA is not bootstrapped inside the popup.
        logger.debug('Attempting MSAL popup login', { scopes: API_LOGIN_REQUEST.scopes }, 'AUTH');
        await instance.loginPopup({
          ...API_LOGIN_REQUEST,
          redirectUri: getMsalPopupRedirectUri(),
        });
      }
    } catch (error) {
      logger.authError('sign in', error);
      setTeamsAuthenticated(false);
      throw error;
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      if (isInTeams || isEmbedded) {
        // Just clear host session state — popup logout is blocked inside a host webview
        setTeamsAuthenticated(false);
        setUser(null);
        setRoles([]);
      } else {
        // MSAL logout
        await instance.logoutPopup();
        setUser(null);
        setRoles([]);
      }
    } catch (error) {
      logger.authError('sign out', error);
      throw error;
    }
  };

  const refreshUserData = async (): Promise<void> => {
    setIsLoading(true);
    await fetchUserData();
    setIsLoading(false);
  };

  // Detect Teams context on mount
  useEffect(() => {
    const detectTeams = async () => {
      try {
        const state = await teamsAuth.initializeContext();
        const inTeams = state.isInTeams;
        setIsInTeams(inTeams);
        setIsEmbedded(state.isEmbedded);
        setTeamsContext(inTeams || state.isEmbedded); // Inform graph.ts about host context
        logger.debug('Host detection completed', {
          isInTeams: inTeams,
          isEmbedded: state.isEmbedded,
          hostName: state.hostName,
          hostClientType: state.hostClientType,
        }, 'AUTH');

        // Auto-login with host SSO whenever we are inside an M365 host
        if ((inTeams || state.isEmbedded) && !teamsAuthenticated) {
          logger.debug('Auto-login: Attempting host SSO', undefined, 'AUTH');
          try {
            const token = await teamsAuth.getAuthToken();
            logger.debug('Auto-login: Host SSO token acquired', { hasToken: !!token }, 'AUTH');
            setTeamsAuthenticated(true);
            // Fetch user data will happen in the next useEffect when isAuthenticated changes
          } catch (error) {
            logger.error('Auto-login: Host SSO failed', error, 'AUTH');
            // Don't set teamsAuthenticated, user will need to click button
          }
        }
      } catch (error) {
        // Detection must never reject: an unhandled rejection here previously
        // propagated to the global handler and blanked the page.
        logger.error('Host detection failed', error, 'AUTH');
      } finally {
        // Mark detection settled regardless of outcome so the app can stop
        // showing the neutral bootstrap spinner and render the correct branch.
        setTeamsDetected(true);
      }
    };
    void detectTeams();
  }, []);

  // Wire up the API auth provider so all API calls automatically include tokens
  useEffect(() => {
    if (isAuthenticated && getToken) {
      logger.debug('Setting up API auth header provider', undefined, 'AUTH');
      setAuthHeaderProvider(getToken);
      setOrderAuthHeaderProvider(getToken);
      setGraphAuthHeaderProvider(getToken); // Also set for graph.ts presence API
      
      // NOTE: Do NOT set Graph token providers in Teams context
      // Teams SSO token has audience for QuickDial API, not Graph API
      // Presence API requires Graph API audience - let it use MSAL instead
      if (isInTeams) {
        logger.debug('Teams context: Graph API will use MSAL (not Teams SSO)', undefined, 'AUTH');
      }
      
      // Always clear token providers to use MSAL for Graph API calls
      setGraphTokenProvider(null);
      setTeamsPresenceTokenProvider(null);
    }
  }, [isAuthenticated, getToken, isInTeams]);

  // Initialize user data when authentication state changes
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      if (isAuthenticated) {
        await fetchUserData();
      } else {
        setUser(null);
        setRoles([]);
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, [isAuthenticated, azureAccount]);

  const contextValue: AuthContextType = {
    // Authentication state
    isAuthenticated,
    isLoading,
    isInTeams, // Now actually detected from Teams SDK!
    isEmbedded,
    teamsDetected,
    isMsalInteracting: inProgress !== InteractionStatus.None,
    
    // User information
    user,
    azureAccount,
    
    // Permission checking
    hasPermission: checkPermission,
    canEditContact: checkCanEditContact,
    canManageList: checkCanManageList,
    canAccessSites: checkCanAccessSites,
    canAccessAdmin: checkCanAccessAdmin,
    
    // Legacy compatibility
    roles,
    
    // Actions
    login: signIn,     // Alias for compatibility
    logout: signOut,   // Alias for compatibility
    signIn,
    signOut,
    refreshUserData,
    getToken
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
